import { useState, useEffect, useRef } from 'react';
import { App as CapApp } from '@capacitor/app';
import { isNative } from '../utils/platformUtils';
import { useDataContext } from '../data/DataProvider';
import { completeOAuthReturn, getSessionUser, startGoogleSignIn, readOAuthMode } from '../lib/authAccount';
import { track } from '../utils/analytics';
import { clearNewsletterOptIn, rememberNewsletterOptIn, subscribeToNewsletter, takeNewsletterOptIn } from '../lib/newsletter';

// Retour dans l'app apres l'autorisation Google (linkIdentity), sur les deux plateformes :
//  - web   : Supabase redirige sur l'origine avec ?code=<pkce> (ou ?error=...) ;
//  - natif : la redirection saute vers upscale://auth-callback?code=..., recue par appUrlOpen.
//
// Dans les deux cas on echange le code contre la session liee (meme user_id, donnees
// conservees), puis on recharge l'app : le boot re-pull les donnees sous l'identite
// desormais permanente et l'etat premium se met a jour.
//
// Si l'email Google a deja un compte (reinstallation, autre appareil, compte cree par
// code email), le rattachement est refuse avec error_code=email_exists : on enchaine
// alors sur startGoogleSignIn, qui connecte au compte existant.
//
// Distinct de usePatreonReturn (?patreon= / upscale://patreon) : params et host differents,
// aucun conflit au boot.
//
// Natif, deux livraisons possibles pour la meme URL :
//  - a chaud : l'app tournait encore, appUrlOpen se declenche ;
//  - a froid : iOS a tue l'app pendant l'auth (le SFSafariViewController prend la memoire),
//              le retour relance l'app et l'URL n'arrive QUE par getLaunchUrl(). Sans cette
//              lecture, le login est perdu et l'app a l'air de n'avoir rien fait.

const ERROR_KEYS = ['error', 'error_code', 'error_description'];
// Retours de linkIdentity qui veulent dire "ce compte Google appartient deja a un user".
const LINK_TAKEN_CODES = ['email_exists', 'identity_already_exists'];

// Le code PKCE arrive en query, les erreurs dans le fragment (#error=...&error_code=...),
// avec parfois error_code duplique en query : on lit les deux.
function readWebCode(location = window.location) {
  const params = new URLSearchParams(location.search);
  const hash = new URLSearchParams(location.hash.replace(/^#/, ''));
  const pick = (key) => params.get(key) || hash.get(key);

  const code = params.get('code');
  const errorCode = pick('error_code');
  const error = pick('error') || pick('error_description');
  if (!code && !error && !errorCode) return null;

  params.delete('code');
  ERROR_KEYS.forEach((key) => params.delete(key));
  const search = params.toString();
  // Le fragment ne sert qu'au retour OAuth : on ne le garde que s'il porte autre chose.
  const keepHash = ERROR_KEYS.some((key) => hash.has(key)) ? '' : location.hash;
  window.history.replaceState({}, '', `${location.pathname}${search ? `?${search}` : ''}${keepHash}`);
  return { code, error, errorCode };
}

// Meme lecture que sur le web, et pour la meme raison : GoTrue rend le code en query mais
// ses erreurs dans le fragment. Le parseur natif ne lisait que la query, donc un refus de
// rattachement revenait vide et le hook sortait sans rien dire.
function parseNativeCode(url) {
  let parsed;
  try {
    parsed = new URL(url);
  } catch {
    return null;
  }
  if (parsed.host !== 'auth-callback' && parsed.pathname.replace(/\//g, '') !== 'auth-callback') return null;
  const params = parsed.searchParams;
  const hash = new URLSearchParams(parsed.hash.replace(/^#/, ''));
  const pick = (key) => params.get(key) || hash.get(key);
  return {
    code: params.get('code'),
    error: pick('error') || pick('error_description'),
    errorCode: pick('error_code'),
  };
}

// Un code PKCE ne s'echange qu'une fois : rejoue, il rend une erreur alors que le login
// a reussi. Les deux gardes ci-dessous couvrent les deux facons de le rejouer.

// 1. Meme URL livree deux fois dans la meme session JS (getLaunchUrl + appUrlOpen au
//    demarrage a froid). Au niveau module, donc partagee par tous les montages du hook.
const handledUrls = new Set();

// 2. Le reload de fin de flow repart d'un module vierge, alors que getLaunchUrl() rend
//    toujours l'URL du lancement : sans marqueur survivant au reload, on re-echangerait
//    un code deja consomme. sessionStorage a exactement la bonne duree de vie, il meurt
//    avec la webview donc un vrai lancement a froid repart bien de zero.
const LAUNCH_READ_KEY = 'upscale_oauth_launch_read';

// Le repli rouvre un navigateur alors qu'on vient de fermer le precedent. Sur Android les
// deux BrowserControllerActivity se marchent dessus : la fermeture de l'ancienne prend
// plus de 500 ms et tombe pendant l'ouverture de la nouvelle, qui meurt dans la
// transition. L'utilisateur voit une page s'ouvrir, clignoter et disparaitre, et le flux
// s'arrete la. Constate sur la Lenovo le 19/08/2026. On attend donc la fermeture
// effective avant de rouvrir. Deux details rendent cette attente moins evidente qu'elle
// n'en a l'air. browserFinished est emis des la demande de fermeture, pas a la fin de
// l'activite : sur la Lenovo il arrive 260 ms avant que celle-ci ne se termine vraiment.
// Et BrowserControllerActivity est en singleTask, donc il n'en existe qu'une instance :
// le navigateur rouvert reutilise celle qu'on est en train de fermer, et disparait avec
// elle. On laisse donc une marge apres l'evenement. Le garde-fou de 1,5 s couvre le cas
// ou le navigateur etait deja ferme, ou l'evenement n'arrive jamais.
const BROWSER_CLOSE_MARGIN_MS = 800;

// L'échange a besoin de la base locale, mais elle peut ne jamais arriver : une base
// déjà dans une version plus récente (build TestFlight puis version du store) laisse
// `db` à null pour de bon. On l'attendait indéfiniment, donc le voile "Signing you
// in..." ne se levait jamais. Passé ce délai on échange sans elle : completeOAuthReturn
// sait faire, la purge locale du repli signin est simplement sautée.
const DB_WAIT_MS = 3000;

// Navigateur du repli refermé sans être allé au bout : browserFinished arrive, aucun
// callback ne suit. Le délai laisse le temps au deep link d'arriver quand la fermeture
// est justement celle qui accompagne un retour réussi.
const BROWSER_CLOSED_GRACE_MS = 2000;

let browserClosed = null;

function closeBrowserAndWait(Browser) {
  return new Promise((resolve) => {
    let listener = null;
    const finish = () => {
      clearTimeout(timer);
      listener?.remove?.();
      setTimeout(resolve, BROWSER_CLOSE_MARGIN_MS);
    };
    const timer = setTimeout(finish, 1500);
    Promise.resolve(Browser.addListener('browserFinished', finish)).then((l) => {
      listener = l;
      Browser.close().catch(() => {});
    });
  });
}

function claimUrl(url) {
  if (handledUrls.has(url)) return false;
  handledUrls.add(url);
  return true;
}

// getLaunchUrl() rend l'URL de lancement indefiniment, y compris sans rapport avec l'auth :
// on ne la lit qu'une fois par session de webview, jamais a chaque montage.
function claimLaunchRead() {
  try {
    if (sessionStorage.getItem(LAUNCH_READ_KEY)) return false;
    sessionStorage.setItem(LAUNCH_READ_KEY, '1');
  } catch {
    // Storage indisponible : le dedoublonnage par URL reste actif.
  }
  return true;
}

export function useOAuthReturn() {
  const { db } = useDataContext();
  const [notice, setNotice] = useState(null); // { severity, message }
  const [pending, setPending] = useState(false);
  const [ret, setRet] = useState(null);
  const [dbWaitExpired, setDbWaitExpired] = useState(false);
  const handledRef = useRef(null);
  const capturedRef = useRef(false);
  // Miroir de `ret` : l'attente de fermeture du navigateur tourne dans une closure
  // figée et ne verrait pas le state arriver.
  const retRef = useRef(null);
  const browserWatchRef = useRef(null);

  // Le repli peut laisser un écouteur armé : on le retire au démontage.
  useEffect(() => () => browserWatchRef.current?.(), []);

  useEffect(() => {
    // Web : le code est dans l'URL au premier rendu. readWebCode la nettoie, donc une
    // seule lecture (en dev, StrictMode joue l'effet deux fois et la seconde ne verrait
    // plus rien).
    if (!isNative()) {
      if (!capturedRef.current) {
        capturedRef.current = true;
        const web = readWebCode();
        retRef.current = web;
        setRet(web);
      }
      return undefined;
    }

    // Natif : deep link. Browser.close() referme le SFSafariViewController.
    const consume = async (url) => {
      const parsed = parseNativeCode(url);
      // Une URL de lancement etrangere a l'auth (deep link contenu, notification) s'arrete ici.
      if (!parsed || !claimUrl(url)) return;
      const { Browser } = await import('@capacitor/browser');
      browserClosed = closeBrowserAndWait(Browser);
      retRef.current = parsed;
      setRet(parsed);
    };

    let remove = null;
    CapApp.addListener('appUrlOpen', ({ url }) => consume(url)).then((listener) => {
      remove = () => listener.remove();
    });

    if (claimLaunchRead()) {
      CapApp.getLaunchUrl?.()
        .then((res) => {
          if (res?.url) consume(res.url);
        })
        .catch(() => {});
    }

    return () => remove?.();
  }, []);

  useEffect(() => {
    if (!ret || handledRef.current === ret) return undefined;
    // Un nouveau callback est arrivé : la surveillance du navigateur du repli n'a
    // plus lieu d'être.
    browserWatchRef.current?.();
    // L'échange attend la base locale (le repli signin la purge), mais pas éternellement.
    if (ret.code && !db && !dbWaitExpired) {
      const timer = setTimeout(() => setDbWaitExpired(true), DB_WAIT_MS);
      return () => clearTimeout(timer);
    }
    handledRef.current = ret;

    const fail = (reason) => {
      clearNewsletterOptIn('signup');
      setNotice({ severity: 'error', message: 'Google sign-in failed. Please try again.' });
      track('google_link_failed', { reason });
      setPending(false);
    };

    // Natif : le navigateur du repli peut être refermé sans aller au bout (retour, swipe,
    // refus de Google). Aucun callback n'arrive alors jamais et le voile reste en place
    // pour toujours. On n'arme ce guet qu'après la réouverture : closeBrowserAndWait émet
    // déjà des fermetures programmatiques, qui ne veulent pas dire la même chose. Sur le
    // web il n'y a rien à surveiller, la page part.
    const watchFallbackBrowser = async (startedWith) => {
      if (!isNative() || retRef.current !== startedWith) return;
      const { Browser } = await import('@capacitor/browser');
      let graceTimer = null;
      let listener = null;
      let stopped = false;
      const stop = () => {
        stopped = true;
        clearTimeout(graceTimer);
        listener?.remove?.();
        browserWatchRef.current = null;
      };
      browserWatchRef.current = stop;
      const onFinished = () => {
        graceTimer = setTimeout(() => {
          // Un callback est arrivé entre-temps : cette fermeture accompagnait un retour
          // réussi, l'échange suit son cours.
          if (retRef.current !== startedWith) return;
          stop();
          setPending(false);
          setNotice({ severity: 'warning', message: 'Sign-in was cancelled. Please try again.' });
          track('google_link_failed', { reason: 'browser_closed' });
        }, BROWSER_CLOSED_GRACE_MS);
      };
      listener = await Promise.resolve(Browser.addListener('browserFinished', onFinished));
      if (stopped) listener?.remove?.();
    };

    (async () => {
      if (ret.error || ret.errorCode) {
        // Le compte Google appartient deja a un autre user : email_exists si c'est l'email
        // qui est pris (compte cree par code email), identity_already_exists si l'identite
        // Google elle-meme est deja rattachee. Dans les deux cas le rattachement est
        // impossible, on repart en connexion pure sur ce compte. Une seule fois, sinon on
        // boucle sur la redirection.
        if (LINK_TAKEN_CODES.includes(ret.errorCode) && readOAuthMode() !== 'signin') {
          track('google_link_email_exists');
          setPending(true);
          try {
            // Sans cette attente, le navigateur du repli s'ouvre dans la fermeture du
            // precedent et se referme aussitot.
            if (browserClosed) await browserClosed;
            await startGoogleSignIn();
            watchFallbackBrowser(ret).catch(() => {});
            return; // web : la page part ; natif : le navigateur se rouvre
          } catch {
            fail('signin_fallback');
            return;
          }
        }
        fail(ret.errorCode || ret.error);
        return;
      }
      if (!ret.code) {
        // Un callback d'auth qui ne porte ni code ni erreur ne veut rien dire. Avaler le
        // cas en silence, c'est le "rien ne se passe" impossible a tracer du 19/08/2026 :
        // on le dit, et on logue les cles vues, jamais leurs valeurs (le code PKCE n'a
        // rien a faire dans les logs).
        console.warn('[oauth] callback sans code ni erreur, cles vues:', Object.keys(ret).filter((k) => ret[k]).join(',') || 'aucune');
        fail('empty_callback');
        return;
      }

      setPending(true);
      try {
        const oauthMode = readOAuthMode();
        await completeOAuthReturn(ret.code, db);
        const signedUser = await getSessionUser();
        if (oauthMode === 'link' && takeNewsletterOptIn('signup', signedUser?.id)) {
          try {
            await subscribeToNewsletter();
          } catch {
            rememberNewsletterOptIn('retry', signedUser?.id, 'retry');
            setNotice({ severity: 'warning', message: 'Your account is ready, but the newsletter signup is waiting for a connection.' });
          }
        } else {
          clearNewsletterOptIn('signup');
        }
        track('google_linked');
        window.location.reload();
      } catch (err) {
        // Le toast est generique et l'echange PKCE echoue souvent cote client
        // (code_verifier absent du storage), donc sans requete a retrouver dans
        // les logs GoTrue : sans cette trace, la vraie cause est invisible.
        console.warn('[oauth] echange du code echoue:', err?.message || err);
        fail('exchange');
      }
    })();
    return undefined;
  }, [ret, db, dbWaitExpired]);

  return { notice, pending, dismissNotice: () => setNotice(null) };
}

export default useOAuthReturn;
